<script setup lang="ts">
// Shell-level bid snackbar. Sits above the BottomNav (or pinned to
// the bottom of the viewport on routes without it) and auto-dismisses
// after ~6s. Tapping the body routes to the bid sheet for the item.
//
// One surface, three variants (see BidToastVariant):
//   'outbid'      — a competing bid beat you.
//   'removed'     — an organizer removed your bid (GT1-442). Same design as
//                   the outbid toast, different copy + the "Removed" pill
//                   from BidCard. The admin's reason is NEVER rendered.
//   'now-winning' — that removal promoted you to high bidder (GT1-442).
//                   Borrows BidCard's 'winning' language: success colour and
//                   the "Winning" pill.
import { computed, watch, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { useOutbidToast } from '@/shell/stores/outbidToast';

const store = useOutbidToast();
const router = useRouter();
const visible = ref(false);
let dismissTimer: ReturnType<typeof setTimeout> | null = null;

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const formattedHighBid = computed(() =>
  store.current ? formatter.format(store.current.newHighBidCents / 100) : '',
);

const displayTitle = computed(() => store.current?.itemTitle ?? 'an item you bid on');

const variant = computed(() => store.current?.variant ?? 'outbid');

// Per-variant copy + treatment. Kept as one map so the wording for a void is
// impossible to drift between the icon, the pill and the headline.
const treatment = computed(() => {
  switch (variant.value) {
    case 'removed':
      // §6.1 — "Removed" is the only word a bidder ever sees for a voided
      // bid, and it matches BidCard's warn 'removed' pill.
      return {
        icon: 'pi pi-exclamation-circle',
        iconClass: 'outbid-snackbar__icon--warning',
        title: 'Your bid was removed',
        pill: { severity: 'warn' as const, value: 'Removed' },
        action: 'Bid again',
      };
    case 'now-winning':
      return {
        icon: 'pi pi-check-circle',
        iconClass: 'outbid-snackbar__icon--success',
        title: "You're now the high bidder",
        pill: { severity: 'success' as const, value: 'Winning' },
        action: 'View',
      };
    default:
      return {
        icon: 'pi pi-bell',
        iconClass: '',
        title: "You've been outbid",
        pill: null,
        action: 'Bid again',
      };
  }
});

// Amount lead-in mirrors BidCard's bottom row: the promoted bidder is looking
// at their OWN bid ("Your bid"), the other two at the price to beat ("now").
const detail = computed(() => {
  const title = displayTitle.value;
  if (variant.value === 'now-winning') return `${title} · your bid ${formattedHighBid.value}`;
  // A removal can leave the entity with no bids at all — "now $0" would be a
  // lie about the price, so say so plainly.
  if (variant.value === 'removed' && (store.current?.newHighBidCents ?? 0) === 0) {
    return `${title} · no bids yet`;
  }
  return `${title} · now ${formattedHighBid.value}`;
});

function clearTimer(): void {
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
}

// Schedule an auto-dismiss when a new toast comes in. Keep `visible`
// independent from store.current so the leave-transition can finish
// after the underlying payload is cleared.
watch(
  () => store.current,
  (next) => {
    clearTimer();
    if (next) {
      visible.value = true;
      dismissTimer = setTimeout(() => {
        visible.value = false;
        // Match leave-transition duration before clearing the payload.
        setTimeout(() => store.dismiss(), 320);
      }, 6000);
    } else {
      visible.value = false;
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  clearTimer();
});

function onTap(): void {
  const payload = store.current;
  if (!payload) return;
  clearTimer();
  visible.value = false;
  const isBundle = payload.itemId == null;
  const targetId = payload.itemId ?? payload.bundleId;
  const entity = isBundle ? 'bundle' : 'item';
  // The promoted bidder already leads — opening the bid sheet would invite
  // them to bid against themselves. Land them on the detail page instead.
  const openBidSheet = payload.variant !== 'now-winning';
  router.push({
    path: `/${payload.eventSlug}/${isBundle ? 'bundles' : 'items'}/${targetId}`,
    ...(openBidSheet ? { query: { bid: `${entity}:${targetId}` } } : {}),
  });
  // Clear after the transition.
  setTimeout(() => store.dismiss(), 320);
}

function onDismiss(event: Event): void {
  event.stopPropagation();
  clearTimer();
  visible.value = false;
  setTimeout(() => store.dismiss(), 320);
}
</script>

<template>
  <Transition name="outbid-snackbar">
    <div
      v-if="visible && store.current"
      class="outbid-snackbar"
      role="button"
      tabindex="0"
      data-testid="outbid-snackbar"
      :data-variant="variant"
      @click="onTap"
      @keydown.enter.prevent="onTap"
      @keydown.space.prevent="onTap"
    >
      <span
        :class="['outbid-snackbar__icon', treatment.iconClass]"
        aria-hidden="true"
      >
        <i :class="treatment.icon" />
      </span>
      <span class="outbid-snackbar__body">
        <span class="outbid-snackbar__headline">
          <span class="outbid-snackbar__title">{{ treatment.title }}</span>
          <!-- Same pill vocabulary as BidCard, so the state word a bidder
               sees in the snackbar matches the one on their bid list. -->
          <Tag
            v-if="treatment.pill"
            :severity="treatment.pill.severity"
            :value="treatment.pill.value"
            data-testid="outbid-snackbar__pill"
          />
        </span>
        <span class="outbid-snackbar__detail">{{ detail }}</span>
      </span>
      <span
        class="outbid-snackbar__action"
        aria-hidden="true"
      >{{ treatment.action }}</span>
      <Button
        icon="pi pi-times"
        text
        rounded
        aria-label="Dismiss"
        class="outbid-snackbar__dismiss"
        @click="onDismiss"
      />
    </div>
  </Transition>
</template>

<style scoped>
.outbid-snackbar {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: var(--app-space-12);
  width: 100%;
  text-align: left;
  background: var(--app-text-primary, #1a1a1a);
  color: #fff;
  border: 0;
  border-radius: var(--app-radius-lg);
  padding: var(--app-space-12) var(--app-space-16);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  font: inherit;
}
.outbid-snackbar:focus-visible {
  outline: 2px solid var(--app-border-focus);
  outline-offset: 2px;
}
.outbid-snackbar__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  font-size: var(--app-font-size-body-lg);
}
/* Colour treatment per variant. Only the glyph is tinted — the circle keeps
   the translucent-white base so contrast on the dark surface stays legible
   whatever the token resolves to in light/dark. */
.outbid-snackbar__icon--success {
  color: var(--app-success);
}
.outbid-snackbar__icon--warning {
  color: var(--app-warning);
}
.outbid-snackbar__body {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-4);
  min-width: 0;
}
.outbid-snackbar__headline {
  display: flex;
  align-items: center;
  gap: var(--app-space-8);
  min-width: 0;
}
.outbid-snackbar__title {
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  font-weight: var(--app-font-weight-semibold);
}
.outbid-snackbar__detail {
  font-size: var(--app-font-size-body-sm);
  line-height: var(--app-line-height-body-sm);
  color: rgba(255, 255, 255, 0.72);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.outbid-snackbar__action {
  font-size: var(--app-font-size-body-sm);
  line-height: var(--app-line-height-body-sm);
  font-weight: var(--app-font-weight-semibold);
  color: #fff;
}
.outbid-snackbar__dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  border: 0;
  cursor: pointer;
  padding: 0;
  font-size: var(--app-font-size-caption);
}
.outbid-snackbar__dismiss:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
.outbid-snackbar-enter-active,
.outbid-snackbar-leave-active {
  transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 320ms ease;
}
.outbid-snackbar-enter-from,
.outbid-snackbar-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
